'use client';

import { memo, ComponentProps } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import {
    GlowClickable,
    GlowMenuButton,
    GlowTextBodySmall,
    PropsWithAriaLabel,
    PropsWithClassName,
    PropsWithDataLayer,
} from '../../../../src';
import { Accordion } from './Accordion';
import { GlowAccordion } from './GlowAccordion';

type GlowMenuAccordionListItemProps = PropsWithClassName &
    PropsWithDataLayer & {
        title: string;
        href?: string;
        titleReferent?: string;
        labelColor?: string;
        contents: Accordion[];
        isCollapsible: boolean;
        isTitleBold: boolean;
    };

/**
 * This component is used to display the accordion-style category menu.
 * A component that renders an accordion.
 * @param title - The title of the accordion.
 * @param href - The href of the title.
 * @param titleReferent - The title for the anchor. Used when the label is not descriptive enough.
 * @param labelColor - The color of the title.
 * @param contents - The contents of the accordion.
 * @param isCollapsible - A boolean that determines if the accordion is collapsible.
 * @param isTitleBold - A boolean that determines if the title is bold.
 * @param dataLayer - The data layer component.
 * @param className - Additional styles
 */
export const GlowMenuAccordionListItem: ComponentMemo<GlowMenuAccordionListItemProps> = memo(
    ({ title, href, titleReferent, labelColor, contents, isCollapsible, isTitleBold, dataLayer, className }) => {
        return (
            <GlowAccordion
                title={title}
                href={href}
                titleReferent={titleReferent}
                labelColor={labelColor}
                contents={contents}
                isCollapsible={isCollapsible}
                titleClassName={clsx('bg-surface-primary p-1 desktop:hover:text-underline', className)}
                itemContainerClassName={'bg-surface-primary desktop:hover:text-underline'}
                TitleComponent={TitleComponent}
                ItemComponent={MenuAccordionItem}
                isTitleBold={isTitleBold}
                containerClassName={'desktop:w-menuAccordionItemContainer overflow-hidden'}
                ContainerTagName={'li'}
                dataLayer={dataLayer}
            />
        );
    },
);

type MenuAccordionItemProps = PropsWithClassName &
    PropsWithAriaLabel &
    PropsWithDataLayer & {
        label: string;
        labelColor?: string;
        href: string;
        titleReferent?: string;
    };

const MenuAccordionItem: ComponentMemo<MenuAccordionItemProps> = memo(
    ({ label, labelColor, href, titleReferent, className, ariaLabel, dataLayer }) => {
        return (
            <GlowClickable
                className={clsx('flex-1', 'px-2 py-0.75 desktop:px-0 desktop:py-0.5', className, dataLayer?.className)}
                style={{ color: labelColor }}
                ariaLabel={ariaLabel}
                href={href}
                titleReferent={titleReferent}
                dataLayer={dataLayer}
                content={{
                    labelElement: (
                        <GlowTextBodySmall
                            text={label}
                            className={'text-wrap text-left'}
                            style={labelColor ? { color: 'inherit' } : undefined}
                        />
                    ),
                }}
            />
        );
    },
);

const TitleComponent = memo((props: ComponentProps<typeof GlowMenuButton>) => (
    <GlowMenuButton {...props} fontSize={'bodySmall'} />
));

TitleComponent.displayName = 'TitleComponent';
MenuAccordionItem.displayName = 'MenuAccordionItem';
GlowMenuAccordionListItem.displayName = 'GlowMenuAccordionListItem';
